// The guided tour format (contract section 5): migration, the implicit legacy tour, and the recipes.
// "guided-tour" in file and schema names: schemas/tour.schema.json is the 360 interior tour, a different thing.
import { isObj, monthDay, SEASONS } from './values.js';
/** @typedef {import('./typedefs.js').Tour} Tour */
/** @typedef {import('./typedefs.js').Stop} Stop */
/** @typedef {import('./typedefs.js').Recipe} Recipe */

export const TOUR_SCHEMA = 1;
export const LEGACY_TOUR_ID = 'tur_legacy';

/**
 * Today's `resort.tour: [place keys]` as an ordinary tour, played exactly as on main: each key one stop, the place
 * card open, a flight to the place and a slow orbit, 8 s per stop counted from the start of the flight, looping.
 * Stops whose place does not exist or is out of season are skipped by the tour engine, as today.
 * @param {string[]} keys
 * @returns {Tour}
 */
export function legacyTour(keys) {
  const list = (Array.isArray(keys) ? keys : []).filter((k) => typeof k === 'string' && k);
  return {
    schema: TOUR_SCHEMA, id: LEGACY_TOUR_ID, slug: 'guided-tour', name: { en: 'Guided tour' }, loop: true,
    stops: list.map((place, i) => ({
      id: `s${i + 1}`, at: { place }, move: 'fly-orbit', stay: 8, card: true,
      moveOptions: { orbitRate: 0.08, stayIncludesTravel: true },
    })),
    end: { action: 'none', caption: null },
  };
}

/**
 * Brings a stored tour to the current schema and fills what a reader relies on (a schema number, `loop`, `end`,
 * and per stop an id, a move and a stay). Unknown fields are kept; the input is not changed. A bare list of place
 * keys (today's `resort.tour`) becomes the legacy tour. Anything without a list of stops becomes null.
 * @param {object | string[] | null} doc
 * @returns {Tour | null}
 */
export function migrateTour(doc) {
  if (Array.isArray(doc)) return legacyTour(doc);
  if (!isObj(doc) || !Array.isArray(doc.stops)) return null;
  const out = JSON.parse(JSON.stringify(doc));
  // Schema 1 is the first, so there is no step yet: the step from 1 to 2 goes here when schema 2 arrives. A tour
  // written by a newer build keeps its number and is read as it is (unknown fields kept).
  if (!Number.isInteger(out.schema) || out.schema < 1) out.schema = TOUR_SCHEMA;
  if (typeof out.loop !== 'boolean') out.loop = false;
  if (!isObj(out.end)) out.end = { action: 'home', caption: null };
  const ids = new Set(out.stops.map((s) => s?.id).filter((id) => typeof id === 'string' && id));
  out.stops = out.stops.filter(isObj).map((s, i) => {
    let id = s.id;
    for (let n = i + 1; typeof id !== 'string' || !id; n++) if (!ids.has(`s${n}`)) { id = `s${n}`; ids.add(id); }
    return { ...s, id, move: typeof s.move === 'string' && s.move ? s.move : 'fly-orbit', stay: s.stay ?? 'medium' };
  });
  return out;
}

/**
 * VIS-226: what each tour decides for itself: what shows at a stop (`show`) and how its short text looks (`textLook`),
 * what a touch does while it plays (`touch`: `stop` = it stops where the visitor can do something with the map,
 * `keep` = it plays on), and the seconds without a touch before it plays again (`idleStart`, 0 = never), and whether the map's location pins
 * (`pins`) and the progress steps (`steps`) show while it plays. `from` is
 * the preset key that held the value for every tour before: a tour without its own value uses it, so maps made
 * before the change play as they did.
 */
export const TOUR_OWN = Object.freeze({
  // owner 2026-10-31 (Q5 A "only the words"): a tour that never chose shows its words, not the place card
  show: Object.freeze({ values: Object.freeze(['card', 'text', 'none']), from: 'tours.show', default: 'text' }),
  textLook: Object.freeze({ values: Object.freeze(['lower', 'subtitle', 'top']), from: 'tours.textLook', default: 'lower' }),
  touch: Object.freeze({ values: Object.freeze(['stop', 'keep']), from: null, default: 'stop' }),
  idleStart: Object.freeze({ min: 0, max: 3600, from: 'tours.idleStart', default: 0 }),
  // the owner 2026-10-31: a tour can play without the map's location pins (a cleaner look; the visitor's own map keeps
  // what the view's settings say) and without the progress steps at the bottom
  pins: Object.freeze({ values: Object.freeze([true, false]), from: null, default: true }),
  steps: Object.freeze({ values: Object.freeze([true, false]), from: null, default: true }),
});
const ownOk = (spec, v) => (spec.values ? spec.values.includes(v) : Number.isFinite(v) && v >= spec.min && v <= spec.max);
const ownOut = (spec, v) => (spec.values ? v : Math.round(v));

/**
 * The tour's own value when valid, else the preset's old value (`get(TOUR_OWN[key].from)`) when valid, else the
 * default. `get` is (settingKey) => value: runtime.get in the engine, the editor's valueOf in the portal.
 * @param {Tour | null | undefined} tour
 * @param {'show' | 'textLook' | 'touch' | 'idleStart' | 'pins' | 'steps'} key
 * @param {(settingKey: string) => any} [get]
 * @returns {any}
 */
export function tourOwn(tour, key, get) {
  const spec = TOUR_OWN[key];
  if (!spec) return undefined;
  const own = isObj(tour) ? tour[key] : undefined;
  if (ownOk(spec, own)) return ownOut(spec, own);
  const old = spec.from && typeof get === 'function' ? get(spec.from) : undefined;
  return ownOk(spec, old) ? ownOut(spec, old) : spec.default;
}

const tour = (recipe, stops) => ({ schema: TOUR_SCHEMA, id: '', slug: recipe, name: {}, recipe, loop: false, stops, end: { action: 'home', caption: null } });
const strings = (a) => [...new Set((Array.isArray(a) ? a : []).filter((s) => typeof s === 'string' && s))];
const set = (phase, key, to, ease) => ({ phase, set: key, to, ease });

/**
 * Generators of ordinary tours from what the editor has at hand: the map's place list and the seasons the preset
 * offers. The caller gives the result an id (`newId('tur')`), a name and captions. Structure only: the stay
 * lengths and eases are tuned by eye later. Without the options in `needs` a recipe returns a tour with no stops.
 * @type {Readonly<{ [id: string]: Recipe }>}
 */
export const recipes = Object.freeze({
  // "Seasons at the main building": fly to one place (`place`, or the first of `places`), then stay there while the
  // season changes through `seasons` in the order given. `dates: { <season>: "MM-DD" }` moves the light with it.
  seasons: Object.freeze({
    id: 'seasons', label: 'recipe.seasons', needs: ['place', 'seasons'],
    /** @param {{ place?: string, places?: string[], seasons?: string[], dates?: { [season: string]: string } }} [opts] */
    build(opts = {}) {
      const place = strings([opts.place, ...strings(opts.places)])[0], seasons = strings(opts.seasons).filter((s) => SEASONS.includes(s));
      if (!place) return tour('seasons', []);
      return tour('seasons', seasons.map((season, i) => {
        const day = monthDay(opts.dates?.[season]), phase = i ? 'stay' : 'travel', ease = i ? 'smooth' : 'cut';
        return {
          id: `s${i + 1}`, at: i ? { same: true } : { place }, move: i ? 'push-in' : 'fly-orbit', stay: 'medium', card: i === 0,
          changes: [set(phase, 'season.current', season, ease), ...(day ? [set(phase, 'time.date', day, ease)] : [])], caption: null,
        };
      }));
    },
  }),
  // "Night falls over the cabins": arrive in daylight at a view or a place, the hour runs into the night, the
  // northern lights rise, then up to three more of `places` are visited under them. With `seasons` it plays in
  // the darkest one offered (no night falls under the midnight sun).
  nightfall: Object.freeze({
    id: 'nightfall', label: 'recipe.nightfall', needs: ['place'],
    /** @param {{ place?: string, places?: string[], view?: object, seasons?: string[], fromHour?: number, toHour?: number, level?: string }} [opts] */
    build(opts = {}) {
      const places = strings([opts.place, ...strings(opts.places)]);
      const at = isObj(opts.view) ? { view: opts.view } : places.length ? { place: places[0] } : null;
      if (!at) return tour('nightfall', []);
      const from = Number.isFinite(opts.fromHour) ? opts.fromHour : 14, to = Number.isFinite(opts.toHour) ? opts.toHour : 23;
      const season = ['winter', 'autumn', 'spring'].find((s) => strings(opts.seasons).includes(s));
      return tour('nightfall', [
        { id: 's1', at, move: 'fly-orbit', stay: 'short', changes: [...(season ? [set('travel', 'season.current', season, 'cut')] : []), set('travel', 'time.hour', from, 'smooth'), set('travel', 'aurora.on', false, 'cut')], caption: null },
        { id: 's2', at: { same: true }, move: 'push-in', stay: 'long', changes: [set('stay', 'time.hour', to, 'lapse')], caption: null },
        { id: 's3', at: { same: true }, move: 'push-in', stay: 'long', changes: [set('stay', 'aurora.on', true, 'smooth'), set('stay', 'aurora.level', typeof opts.level === 'string' ? opts.level : 'storm', 'smooth')], caption: null },
        ...places.slice(isObj(opts.view) ? 0 : 1, isObj(opts.view) ? 3 : 4).map((place, i) => ({ id: `s${i + 4}`, at: { place }, move: 'fly-orbit', stay: 'medium', card: true, caption: null })),
      ]);
    },
  }),
});
